'use client'
// =============================================================================
// HudIcon — jeu d'icônes cohérent du tableau de bord. Les images de public/icon/ sont réutilisées telles quelles (projets, processus,
// agents IA, tâches, dossier, courriel, contacts, documentation, assistance, tarifs) ; les autres sont des SVG au même trait (cyan, 1.7 px, bouts arrondis).
// Le mode clair inverse la page et ré-inverse les images : seules les variantes « dark » des icônes sont utilisées.
// =============================================================================

export type HudIconName = 'projets' | 'agents' | 'taches' | 'fichiers' | 'missions' | 'processus' | 'reseau' | 'radar' | 'systeme' | 'analyse' | 'salutations'
  | 'organisations' | 'connectes' | 'horsligne' | 'administration' | 'ajouter' | 'image'
  | 'dossier' | 'courriel' | 'contacts' | 'documentation' | 'assistance' | 'tarifs'

const IMG: Partial<Record<HudIconName, string>> = {
  projets: '/icon/projets.png',
  agents: '/icon/IA_Projet.png',   // corrigé le 2026-10-13 : /icon/IA.png n'existe pas dans public/icon (lien cassé)
  taches: '/icon/taches.png',
  dossier: '/icon/dossier.png',
  courriel: '/icon/courriel.png',
  contacts: '/icon/reseau_dark.png',
  documentation: '/icon/documentation_dark.png',
  assistance: '/icon/assistance_technique_dark.png',
  tarifs: '/icon/panier.png',
  processus: '/icon/process.png',
  salutations: '/icon/IA_Systeme.png',
  analyse: '/icon/analyse.png',
}

const PATHS: Partial<Record<HudIconName, React.ReactNode>> = {
  taches: <><rect x="4" y="3.5" width="16" height="17" rx="2.5" /><path d="M8 9.5l2 2 3.5-4" /><path d="M8 15.5h8" /></>,
  fichiers: <><path d="M3.5 7.5a2 2 0 0 1 2-2h4l2 2.5h7a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" /><path d="M3.5 11h17" /></>,
  missions: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /><path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" /></>,
  reseau: <><circle cx="12" cy="5" r="2.2" /><circle cx="5" cy="18" r="2.2" /><circle cx="19" cy="18" r="2.2" /><path d="M10.7 6.8L6.3 16M13.3 6.8l4.4 9.2M7.2 18h9.6" /></>,
  radar: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><path d="M12 12L18.5 5.5" /><circle cx="15.5" cy="9" r="1" /></>,
  organisations: <><path d="M5 21V5.5a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 15 5.5V21" /><path d="M15 10h3.5A1.5 1.5 0 0 1 20 11.5V21" /><path d="M3 21h18" /><path d="M8.5 8h3M8.5 12h3M8.5 16h3" /></>,
  connectes: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c.4-3.3 2.8-5.2 6-5.2s5.6 1.9 6 5.2" /><circle cx="17.5" cy="9" r="2.4" /><path d="M16.5 14.3c2.6.2 4.2 1.8 4.5 4.7" /></>,
  horsligne: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.5-3.8 3.2-6 7-6s6.5 2.2 7 6" /><path d="M3.5 3.5l17 17" /></>,
  administration: <><circle cx="12" cy="12" r="3" /><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" /></>,
  ajouter: <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
  image: <><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" /><circle cx="9" cy="10" r="1.6" /><path d="M4 17l5-4.5 3.5 3 3-2.5 4.5 4" /></>,
  systeme: <><rect x="3.5" y="4.5" width="17" height="11" rx="2" /><path d="M8 20h8M12 15.5V20" /><path d="M7 10h3l1.5-2.5 2 5L15 10h2" /></>,
}

export function HudIcon({ name, size = 22, title }: { name: HudIconName; size?: number; title?: string }) {
  const src = IMG[name]
  if (src) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={src} alt={title ?? ''} aria-hidden={title ? undefined : true} width={size} height={size} className="hud-icon hud-icon-img" style={{ width: size, height: size }} />
  }
  return (
    <svg className="hud-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      {PATHS[name] ?? PATHS.systeme}
    </svg>
  )
}
